import React from 'react';
import { ScoreCardTradePlan } from '../types/stockAdvice';
import { formatLseNumericDisplay, isUnavailableDisplayValue } from './stockAdviceFormatting';

interface TradePlanCardProps {
  tradePlan: ScoreCardTradePlan;
  exchange: string;
}

const biasClass = (bias: ScoreCardTradePlan['bias']) => {
  if (bias === 'Long') return 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30';
  if (bias === 'Avoid') return 'bg-rose-500/20 text-rose-300 border-rose-500/30';
  return 'bg-amber-500/20 text-amber-300 border-amber-500/30';
};

export const TradePlanCard: React.FC<TradePlanCardProps> = ({ tradePlan, exchange }) => {
  const values = [tradePlan.entryZone, tradePlan.stopLoss, tradePlan.target1, tradePlan.target2, tradePlan.riskReward, tradePlan.timeframe]
    .filter((v): v is string => Boolean(v));
  const unavailableCount = values.filter((v) => isUnavailableDisplayValue(v)).length;
  const mostlyUnavailable = values.length > 0 && unavailableCount >= Math.ceil(values.length * 0.66);

  return (
    <section className="rounded-3xl border border-indigo-400/20 bg-[#121225] p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-white">Trade Setup</h4>
        <span className={`text-xs px-2 py-1 rounded-full border ${biasClass(tradePlan.bias)}`}>{tradePlan.bias}</span>
      </div>
      {mostlyUnavailable ? (
        <p className="text-xs text-slate-300">Tactical note: provider setup coverage is limited, so only directional bias is shown. Re-validate entries, stops, and targets with live chart levels.</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          <div><p className="text-slate-500">Timeframe</p><p className="text-slate-200">{tradePlan.timeframe}</p></div>
          <div><p className="text-slate-500">Entry Zone</p><p className="text-slate-200">{formatLseNumericDisplay(tradePlan.entryZone, exchange)}</p></div>
          <div><p className="text-slate-500">Stop Loss</p><p className="text-slate-200">{formatLseNumericDisplay(tradePlan.stopLoss, exchange)}</p></div>
          <div><p className="text-slate-500">Target 1</p><p className="text-slate-200">{formatLseNumericDisplay(tradePlan.target1, exchange)}</p></div>
          <div><p className="text-slate-500">Target 2</p><p className="text-slate-200">{tradePlan.target2 ? formatLseNumericDisplay(tradePlan.target2, exchange) : '—'}</p></div>
          <div><p className="text-slate-500">Risk/Reward</p><p className="text-slate-200">{tradePlan.riskReward}</p></div>
          <div className="col-span-2"><p className="text-slate-500">Invalidation</p><p className="text-slate-200">{tradePlan.invalidation}</p></div>
        </div>
      )}
    </section>
  );
};
